import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import CustomSVGs from '../customSVGs/CustomSVGs';
import {
  arrowHeadRightLightSVG,
  funnelSVG,
  search2SVG,
  tickSVG,
} from '../../lib/allCustomSVGs';
import '../../styles/boxes/filterBox.scss';

interface FilterBoxProps {
  filterOptions: string[];
  onSearchChange?: (value: string) => void;
  onFilterChange?: (selected: string) => void;
  initialFilter?: string;
  placeholder?: string;
  extraClassName?: string;
  /**
   * The text shown for an option. Options stay the values passed to
   * `onFilterChange`; translate only what is displayed.
   */
  getOptionLabel?: (option: string) => string;
}

const FilterBox: React.FC<FilterBoxProps> = ({
  filterOptions,
  onSearchChange,
  onFilterChange,
  initialFilter = '',
  placeholder = 'Search...',
  extraClassName = '',
  getOptionLabel = (option) => option,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(
    initialFilter || filterOptions[0]
  );
  const [menuBox, setMenuBox] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuBox({
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!dropdownOpen) {
      setMenuBox(null);
      return;
    }
    updateMenuPosition();
  }, [dropdownOpen, updateMenuPosition]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [dropdownOpen, updateMenuPosition]);

  const handleOutsideClick = (e: MouseEvent) => {
    const target = e.target as Node;
    if (triggerRef.current?.contains(target)) return;
    if (menuRef.current?.contains(target)) return;
    setDropdownOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleFilterSelect = (option: string) => {
    setSelectedFilter(option);
    onFilterChange?.(option);
    setDropdownOpen(false);
  };

  return (
    <div className={`filterBox ${extraClassName}`}>
      <div className='filterBox__input'>
        <CustomSVGs svg={search2SVG} />
        <input
          type='text'
          placeholder={placeholder}
          className='filterBox__input--field'
          onChange={(e) => onSearchChange?.(e.target.value)}
        />
      </div>

      <div
        className='filterBox__filter'
        ref={triggerRef}
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <CustomSVGs className='filterBox__filter--icon' svg={funnelSVG} />
        <div className='filterBox__filter--text'>
          {getOptionLabel(selectedFilter)}
        </div>
        <CustomSVGs
          className='filterBox__filter--arrow'
          svg={arrowHeadRightLightSVG}
        />
      </div>

      {dropdownOpen &&
        menuBox &&
        createPortal(
          <div
            ref={menuRef}
            className='filterBox__filter--dropdown filterBox__filter--dropdown--portal'
            style={{
              position: 'fixed',
              top: menuBox.top,
              left: menuBox.left,
              width: menuBox.width,
              zIndex: 5000,
            }}
          >
            {filterOptions.map((option) => (
              <div
                key={option}
                className='filterBox__filter--dropdown-item'
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterSelect(option);
                }}
              >
                {selectedFilter === option && <CustomSVGs svg={tickSVG} />}
                {selectedFilter !== option && (
                  <span style={{ width: '1.6rem' }} />
                )}{' '}
                <span>{getOptionLabel(option)}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default FilterBox;
